import {
  ArrowUpRight,
  ShieldCheck,
  Users,
  HeartHandshake,
  Trophy,
  Calendar,
  Clock,
} from "lucide-react";
import { useReveal } from "../lib/useReveal";
import CampCountdown from "../components/site/CampCountdown";

const formatDateLong = (date) =>
  date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatTime = (date) =>
  date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const SIGNUP_URL =
  "https://anc.apm.activecommunities.com/losangelescounty/activity/search/detail/51565?onlineSiteId=0&locale=en-US&from_original_cui=true";

const values = [
  { icon: Users, label: "TEAMWORK" },
  { icon: ShieldCheck, label: "DISCIPLINE" },
  { icon: Trophy, label: "PERSEVERANCE" },
  { icon: HeartHandshake, label: "RESPECT" },
];

const schedule = [
  {
    title: "MORNING DEVOTION",
    detail: "Scripture, prayer, and a focus word for the day.",
  },
  {
    title: "SKILL DEVELOPMENT",
    detail: "Ball-handling, shooting form, footwork, and conditioning drills.",
  },
  {
    title: "CADET TRAINING",
    detail:
      "Posture, formation, leadership exercises, and teamwork challenges.",
  },
  {
    title: "SCRIMMAGE & REFLECTION",
    detail:
      "Live play followed by a coach-led reflection on character lessons learned.",
  },
];

const CAMP_ISO = "2026-10-08T18:00:00";

export default function BasketballCamp() {
  useReveal();

  const campDate = new Date(CAMP_ISO);
  const dateText = formatDateLong(campDate);
  const timeText = formatTime(campDate);
  const monthDayLabel = campDate
    .toLocaleDateString("en-US", { month: "short", day: "numeric" })
    .toUpperCase();

  return (
    <main data-testid="basketball-page" className="bg-[#F4EBDC]">
      {/* HERO */}
      <section
        data-testid="basketball-hero"
        className="relative pt-28 sm:pt-32 pb-16 bg-[#F4EBDC]"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-6">
            <span className="tag">PROGRAM 03 · CADET & BASKETBALL</span>
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#0E0E0E]/70">
              SPORTS · DISCIPLINE · MENTORSHIP
            </span>
          </div>
          <span className="tape" />

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <h1
                className="font-display text-[14vw] sm:text-[10vw] lg:text-[8vw] xl:text-[160px] leading-[0.88] text-[#0E0E0E] reveal"
                data-testid="basketball-headline"
              >
                CADET &
                <br />
                <span className="text-[#FF5A1F]">BASKETBALL</span>
                <br />
                TRAINING.
              </h1>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#0E0E0E]/85 max-w-xl reveal reveal-delay-1">
                A structured sports + discipline program where basketball
                becomes the classroom for teamwork, fitness, accountability,
                perseverance, leadership, and respect.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 reveal reveal-delay-2">
                <a
                  href={SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-base btn-orange"
                  data-testid="basketball-signup-cta"
                >
                  Sign Up for Camp <ArrowUpRight size={14} />
                </a>
                <a
                  href="#registration"
                  className="btn-base btn-ghost"
                  data-testid="basketball-qr-cta"
                >
                  View Sign-Up QR
                </a>
              </div>

              <div className="mt-8 reveal reveal-delay-3">
                <div
                  data-testid="camp-date-card"
                  className="bg-[#0E0E0E] text-[#F4EBDC] border-2 border-[#0E0E0E] p-5 sm:p-6 mb-4"
                  style={{ boxShadow: "6px 6px 0 #FF5A1F" }}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className="sticker"
                      style={{
                        background: "#FF5A1F",
                        color: "#F4EBDC",
                        borderColor: "#F4EBDC",
                        transform: "rotate(-2deg)",
                      }}
                    >
                      NEXT CAMP
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] sm:items-center gap-4 sm:gap-6">
                    <div className="flex items-start gap-3">
                      <Calendar
                        size={22}
                        className="text-[#FFD700] mt-1 shrink-0"
                      />
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.22em] text-[#FFD700]">
                          DATE
                        </p>
                        <p
                          className="font-display text-2xl sm:text-3xl mt-1 leading-tight"
                          data-testid="camp-date-text"
                        >
                          {dateText.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <span className="hidden sm:block w-px h-12 bg-[#F4EBDC]/20" />
                    <div className="flex items-start gap-3">
                      <Clock
                        size={22}
                        className="text-[#FFD700] mt-1 shrink-0"
                      />
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.22em] text-[#FFD700]">
                          TIME
                        </p>
                        <p
                          className="font-display text-2xl sm:text-3xl mt-1 leading-tight"
                          data-testid="camp-time-text"
                        >
                          {timeText}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <CampCountdown targetISO={CAMP_ISO} dateLabel={monthDayLabel} />
              </div>
            </div>

            <div className="lg:col-span-5 reveal reveal-delay-1 relative">
              <div className="photo-frame">
                <img
                  src="/assets/basketball/training-2.png"
                  alt="Camp action"
                  className="w-full aspect-[4/5] object-cover photo-duotone"
                />
              </div>
              <span
                className="sticker absolute -top-4 -left-3"
                style={{ background: "#FF5A1F", color: "#F4EBDC", borderColor: "#0E0E0E" }}
              >
                NOW ENROLLING
              </span>
              <span
                className="sticker absolute -bottom-4 -right-3"
                style={{ transform: "rotate(4deg)" }}
              >
                NO BENCHWARMERS
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES MARQUEE-LIKE STRIP */}
      <section className="bg-[#0E0E0E] text-[#F4EBDC] py-10 sm:py-14 border-y-2 border-[#0E0E0E]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <div
                key={v.label}
                data-testid={`bb-value-${i}`}
                className="flex items-center gap-4 reveal"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#FF5A1F] border-2 border-[#F4EBDC] text-[#0E0E0E]">
                  <Icon size={20} />
                </span>
                <p className="font-display text-2xl sm:text-3xl">{v.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROGRAM DETAILS */}
      <section className="py-24 sm:py-32 bg-[#F4EBDC]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <span className="tag">/ THE DAY</span>
            <h2 className="font-display mt-5 text-[12vw] sm:text-[7vw] lg:text-[4.5vw] text-[#0E0E0E] leading-[0.95]">
              MORE THAN
              <br />
              BASKETBALL.
              <br />
              <span className="text-[#1B4F72]">A TRAINING</span>
              <br />
              GROUND FOR
              <br />
              CHARACTER.
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#0E0E0E]/85">
              Our camp blends rigorous basketball training with cadet-style
              discipline and Christ-centered mentorship. Every day grows young
              athletes into reliable teammates, courageous leaders, and faithful
              stewards.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base btn-ink"
                data-testid="bb-signup-secondary"
              >
                Register Now <ArrowUpRight size={14} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ol className="border-2 border-[#0E0E0E] divide-y-2 divide-[#0E0E0E] bg-[#F4EBDC]">
              {schedule.map((s, i) => (
                <li
                  key={s.title}
                  data-testid={`bb-schedule-${i}`}
                  className="reveal p-6 sm:p-8 grid grid-cols-12 gap-4 sm:gap-6 items-baseline"
                >
                  <span className="col-span-3 sm:col-span-2 big-num text-[4.5rem] sm:text-[6rem] leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="col-span-9 sm:col-span-10">
                    <h3 className="font-display text-2xl sm:text-3xl text-[#0E0E0E]">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-[#0E0E0E]/80 leading-relaxed">
                      {s.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* REGISTRATION (QR) */}
      <section
        id="registration"
        data-testid="bb-registration"
        className="py-24 sm:py-32 bg-[#1B4F72] text-[#F4EBDC] relative overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 reveal relative">
            <div className="bg-[#F4EBDC] border-2 border-[#0E0E0E] p-6 sm:p-8 inline-block">
              <img
                src="/assets/basketball/camp-qr.png"
                alt="Camp Sign-Up QR Code"
                className="block w-full max-w-[340px] aspect-square object-contain"
                data-testid="bb-qr-image"
              />
              <p className="font-mono text-[11px] tracking-[0.22em] mt-4 text-center text-[#0E0E0E]">
                / SCAN TO SIGN UP
              </p>
            </div>
            <span
              className="sticker absolute -top-4 -right-3"
              style={{
                transform: "rotate(6deg)",
                background: "#FF5A1F",
                color: "#F4EBDC",
                borderColor: "#0E0E0E",
              }}
            >
              CAMP / 01
            </span>
          </div>

          <div className="lg:col-span-7 reveal reveal-delay-1">
            <span className="tag" style={{ color: "#FFD700" }}>
              <span className="bg-[#FFD700] w-2 h-2 inline-block" />
              REGISTRATION
            </span>
            <h2 className="font-display mt-5 text-[12vw] sm:text-[8vw] lg:text-[5vw] leading-[0.92]">
              SIGN UP
              <br />
              FOR THE
              <br />
              <span className="bg-[#FFD700] text-[#1B4F72] px-3 py-1 inline-block mt-2">
                CAMP.
              </span>
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed max-w-xl text-[#F4EBDC]/90">
              Scan the QR code with your phone, or use the direct link below to
              fill out the sign-up form. Spots are limited — register early.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base btn-paper text-base sm:text-lg py-5 px-6"
                data-testid="bb-signup-link"
              >
                Open Sign-Up Form <ArrowUpRight size={16} />
              </a>
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase break-all max-w-md text-[#F4EBDC]/75">
                {SIGNUP_URL}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border-2 border-[#0E0E0E] p-5 bg-[#F4EBDC] text-[#0E0E0E]">
                <p className="font-mono text-[11px] tracking-[0.22em]">
                  / QUESTIONS
                </p>
                <a
                  href="tel:+14246107795"
                  className="font-display text-2xl sm:text-3xl mt-2 block"
                  data-testid="bb-phone"
                >
                  424-610-7795
                </a>
              </div>
              <div className="border-2 border-[#0E0E0E] p-5 bg-[#F4EBDC] text-[#0E0E0E]">
                <p className="font-mono text-[11px] tracking-[0.22em]">
                  / EMAIL
                </p>
                <a
                  href="mailto:ydevelopment7@gmail.com"
                  className="font-display text-base sm:text-lg mt-2 block break-all"
                  data-testid="bb-email"
                >
                  ydevelopment7@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-24 sm:py-32 bg-[#F4EBDC]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex items-end justify-between gap-8 flex-wrap reveal">
            <div>
              <span className="tag">/ GALLERY</span>
              <h2 className="font-display mt-5 text-[12vw] sm:text-[8vw] lg:text-[5vw] text-[#0E0E0E] leading-[0.95]">
                DISCIPLINE
                <br />
                IN <span className="text-[#FF5A1F]">MOTION</span>.
              </h2>
            </div>
            <p className="max-w-md text-[#0E0E0E]/80">
              Glimpses from training sessions — where character, sweat, and
              brotherhood meet.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-6">
            <div className="reveal sm:col-span-4 photo-frame">
              <img
                src="/assets/basketball/training-1.jpg"
                alt="Training"
                className="w-full h-full object-cover aspect-[4/3] photo-duotone"
                style={{ objectPosition: "center top" }}
                data-testid="bb-gallery-1"
              />
            </div>
            <div className="reveal reveal-delay-1 sm:col-span-2 photo-frame">
              <img
                src="/assets/basketball/training-3.jpg"
                alt="Training"
                className="w-full h-full object-cover aspect-[4/3] photo-duotone"
                data-testid="bb-gallery-2"
              />
            </div>
            <div className="reveal reveal-delay-2 sm:col-span-3 photo-frame">
              <img
                src="/assets/basketball/training-4.jpg"
                alt="Training"
                className="w-full h-full object-cover aspect-[4/3] photo-duotone"
                data-testid="bb-gallery-3"
              />
            </div>
            <div className="reveal reveal-delay-3 sm:col-span-3 photo-frame">
              <img
                src="/assets/other/community-1.jpg"
                alt="Community"
                className="w-full h-full object-cover aspect-[4/3] photo-duotone"
                data-testid="bb-gallery-4"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
